"use client";

import { c as _c } from "react/compiler-runtime";
import type { Variants } from "framer-motion";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { m, useAnimation } from "framer-motion";
import { cn } from "@/lib/utils";
export interface CalendarDaysIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}
interface CalendarDaysIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}

// Calendar dot positions
const DOTS = [{
  cx: 8,
  cy: 14
}, {
  cx: 12,
  cy: 14
}, {
  cx: 16,
  cy: 14
}, {
  cx: 8,
  cy: 18
}, {
  cx: 12,
  cy: 18
}, {
  cx: 16,
  cy: 18
}];

// Staggered fade animation for dots
const DOT_VARIANTS: Variants = {
  normal: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  },
  animate: (i: number) => ({
    opacity: [1, 0.3, 1],
    transition: {
      delay: i * 0.1,
      duration: 0.4,
      times: [0, 0.5, 1]
    }
  })
};
const CalendarDaysIcon = forwardRef<CalendarDaysIconHandle, CalendarDaysIconProps>((t0, ref) => {
  const $ = _c(34);
  let className;
  let onMouseEnter;
  let onMouseLeave;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      onMouseEnter,
      onMouseLeave,
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onMouseEnter;
    $[3] = onMouseLeave;
    $[4] = props;
    $[5] = t1;
  } else {
    className = $[1];
    onMouseEnter = $[2];
    onMouseLeave = $[3];
    props = $[4];
    t1 = $[5];
  }
  const size = t1 === undefined ? 20 : t1;
  const controls = useAnimation();
  const isControlledRef = useRef(false);
  let t2;
  if ($[6] !== controls) {
    t2 = () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal")
      };
    };
    $[6] = controls;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useImperativeHandle(ref, t2);
  let t3;
  if ($[8] !== controls || $[9] !== onMouseEnter) {
    t3 = e => {
      if (!isControlledRef.current) {
        controls.start("animate");
      }
      onMouseEnter?.(e);
    };
    $[8] = controls;
    $[9] = onMouseEnter;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleMouseEnter = t3;
  let t4;
  if ($[11] !== controls || $[12] !== onMouseLeave) {
    t4 = e_0 => {
      if (!isControlledRef.current) {
        controls.start("normal");
      }
      onMouseLeave?.(e_0);
    };
    $[11] = controls;
    $[12] = onMouseLeave;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleMouseLeave = t4;
  let t5;
  if ($[14] !== className) {
    t5 = cn("inline-flex items-center justify-center", className);
    $[14] = className;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== size) {
    t6 = {
      width: size,
      height: size
    };
    $[16] = size;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t10;
  let t7;
  let t8;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <path d="M8 2v4" />;
    t8 = <path d="M16 2v4" />;
    t9 = <rect width="18" height="18" x="3" y="4" rx="2" />;
    t10 = <path d="M3 10h18" />;
    $[18] = t10;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t10 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t11;
  if ($[22] !== controls) {
    t11 = DOTS.map((dot, index) => <m.circle key={`${dot.cx}-${dot.cy}`} cx={dot.cx} cy={dot.cy} r="1" fill="currentColor" stroke="none" variants={DOT_VARIANTS} initial="normal" animate={controls} custom={index} />);
    $[22] = controls;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== size || $[25] !== t11) {
    t12 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{t7}{t8}{t9}{t10}{t11}</svg>;
    $[24] = size;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== handleMouseEnter || $[28] !== handleMouseLeave || $[29] !== props || $[30] !== t12 || $[31] !== t5 || $[32] !== t6) {
    t13 = <div className={t5} style={t6} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>{t12}</div>;
    $[27] = handleMouseEnter;
    $[28] = handleMouseLeave;
    $[29] = props;
    $[30] = t12;
    $[31] = t5;
    $[32] = t6;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
});
CalendarDaysIcon.displayName = "CalendarDaysIcon";
export { CalendarDaysIcon };